import { useEffect, useState, useCallback, useRef } from "react";
import { Upload, Loader2, Trash2, ArrowDownLeft, ArrowUpRight, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../../lib/api";
import { money, downloadDoc } from "./shared";
import { useI18n } from "../../../context/I18nContext";

export default function Bancos({ companyId, year, month, onDataChange }) {
  const { t } = useI18n();
  const [statements, setStatements] = useState([]);
  const [movements, setMovements] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/accounting/bank/statements?company_id=${companyId}`).then((r) => setStatements(r.data)).catch(() => {});
    const p = new URLSearchParams({ company_id: companyId });
    if (year) p.append("year", year);
    if (month) p.append("month", month);
    api.get(`/accounting/bank/movements?${p}`).then((r) => setMovements(r.data)).catch(() => {});
  }, [companyId, year, month]);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("company_id", companyId);
      fd.append("file", file);
      try {
        const { data } = await api.post("/accounting/bank/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${file.name}: ${t("os.contab.movInterpreted")(data.movement_count)}`);
      } catch (e) { toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail) || t("os.contab.err")}`); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    load();
    onDataChange?.();
  };
  const delStmt = async (id) => {
    if (!window.confirm(t("os.contab.delStmtConfirm"))) return;
    try { await api.delete(`/accounting/bank/statements/${id}`); toast.success(t("os.contab.deleted")); load(); } catch { toast.error(t("os.contab.err")); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-navy/50">{t("os.contab.bankIntro")}</p>
        <input ref={fileRef} type="file" accept=".pdf" multiple hidden data-testid="bank-file-input" onChange={(e) => onFiles(e.target.files)} />
        <button data-testid="bank-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity disabled:opacity-60 shrink-0">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t("os.contab.uploadStatement")}
        </button>
      </div>

      {statements.length > 0 && (
        <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto" data-testid="bank-statements-panel">
          <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_120px_60px] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10">
            <span>{t("os.contab.stmtCols.banco")}</span><span>{t("os.contab.stmtCols.cuenta")}</span><span>{t("os.contab.stmtCols.periodo")}</span><span>{t("os.contab.stmtCols.movimientos")}</span><span /></div>
          {statements.map((s) => (
            <div key={s.id} data-testid={`bank-statement-${s.id}`} className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1fr_120px_60px] gap-3 px-5 py-4 items-center border-b border-navy/5 last:border-0 text-sm">
              <div className="font-medium">{s.bank || "—"}</div>
              <div className="font-mono text-xs text-navy/50">{s.account_number || "—"}</div>
              <div className="text-navy/60">{s.period || "—"}</div>
              <div>{s.movement_count}</div>
              <div className="flex items-center gap-2 justify-self-end">
                {s.storage_path && <button data-testid={`bank-download-${s.id}`} title={t("os.contab.downloadPdf")} onClick={() => downloadDoc("statement", s.id, s.original_filename)} className="text-navy/30 hover:text-violet"><Download className="w-4 h-4" /></button>}
                <button data-testid={`bank-del-${s.id}`} onClick={() => delStmt(s.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {movements.length === 0 ? (
        <div className="text-navy/40 py-12 text-center border border-dashed border-navy/15 rounded-lg" data-testid="bank-empty">
          {t("os.contab.emptyMovements")}
        </div>
      ) : (
        <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto" data-testid="bank-movements-panel">
          <div className="hidden md:grid grid-cols-[95px_60px_1.6fr_1.2fr_130px_110px] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10">
            <span>{t("os.contab.movCols.fecha")}</span><span>{t("os.contab.movCols.hora")}</span><span>{t("os.contab.movCols.concepto")}</span><span>{t("os.contab.movCols.contraparte")}</span><span>{t("os.contab.movCols.monto")}</span><span>{t("os.contab.movCols.tipo")}</span></div>
          {movements.map((m) => (
            <div key={m.id} data-testid={`bank-movement-${m.id}`} className="grid grid-cols-2 md:grid-cols-[95px_60px_1.6fr_1.2fr_130px_110px] gap-3 px-5 py-4 items-center border-b border-navy/5 last:border-0 text-sm">
              <div className="text-xs text-navy/50">{(m.date || "").slice(0, 10)}</div>
              <div className="text-xs text-navy/40">{m.time || "—"}</div>
              <div className="min-w-0 truncate">{m.description || "—"}</div>
              <div className="min-w-0"><div className="truncate text-navy/70">{m.counterparty || "—"}</div><div className="font-mono text-[11px] text-navy/40 truncate">{m.account}</div></div>
              <div className={`font-medium ${m.type === "abono" ? "text-emerald-600" : "text-destructive"}`}>{money(m.amount)}</div>
              <div>
                <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full ${m.type === "abono" ? "bg-emerald-500/10 text-emerald-600" : "bg-destructive/10 text-destructive"}`}>
                  {m.type === "abono" ? <ArrowDownLeft className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />} {m.type === "abono" ? t("os.contab.abono") : t("os.contab.cargo")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
